/* cwclientelements
   copyright 2019 LOGILAB S.A. (Paris, FRANCE), all rights reserved.
   contact http://www.logilab.fr/ -- mailto:contact@logilab.fr
*/

import * as React from 'react';
import {providers} from '@logilab/cwclientlibjs';
import {
    EntityRelationsEditor,
    RelationStatus,
    getEntityRelationStatus,
    EntityRelationsEditorProps,
    EntityRelationsEditorState,
    getRelatedCount,
} from './EntityRelationsEditor';
import {
    DefaultEntityRelationsLookup,
    lookupEntitiesRelatedWithChanges,
    lookupEntitiesRelated,
} from './EntityRelationsEditorLookup';
import {DefaultEntityRelationsSelector} from './EntityRelationsEditorSelector';
import {TranslationContext} from './commons';

/**
 * The props for a relation editor for selecting a single related entity
 */
export interface DefaultEntityRelationsEditorProps
    extends EntityRelationsEditorProps {
    /**
     * The limit when looking up relatable entities
     */
    lookupLimitRelatable: number;
    /**
     * The limit when looking up already related entities
     */
    lookupLimitRelated: number;
}

/**
 * The default props for an editor of entity relations
 */
export const ENTITY_RELATIONS_EDITOR_DEFAULT_PROPS = {
    className: '',
    lookupLimitRelatable: 5,
    lookupLimitRelated: -1,
    baseChanges: {
        added: [],
        removed: [],
    },
    renderLookup: (component: DefaultEntityRelationsEditor) => {
        return (
            <DefaultEntityRelationsLookup
                specification={component.props.specification}
                lookupLimitRelatable={component.props.lookupLimitRelatable}
                lookupLimitRelated={component.props.lookupLimitRelated}
                onLookupLoading={component.onLookupLoading}
                onLookupResult={component.onLookupResult}
                lookupRelatedEntities={(specification, limit) =>
                    lookupEntitiesRelatedWithChanges(specification, limit, [
                        component.props.baseChanges,
                        component.state.changes,
                    ])
                }
            />
        );
    },
    renderRelatable: (
        component: EntityRelationsEditor,
        entity: providers.Entity,
        status: RelationStatus
    ) => {
        return [
            <td key={'candidate-' + entity['@eid'] + '-name'}>
                {entity['name']}
            </td>,
            <td key={'candidate-' + entity['@eid'] + '-selector'}>
                <DefaultEntityRelationsSelector
                    entity={entity}
                    isRelated={
                        status === RelationStatus.Related ||
                        status === RelationStatus.Added
                    }
                    onToggle={component.onToggle}
                />
            </td>,
            <td
                key={'candidate-' + entity['@eid'] + '-status'}
                style={{width: '100px'}}
            >
                {status === RelationStatus.Added ? (
                    <span style={{color: 'green'}}>
                        {component.context.translate('added')}
                    </span>
                ) : status === RelationStatus.Removed ? (
                    <span style={{color: 'red'}}>
                        {component.context.translate('removed')}
                    </span>
                ) : null}
            </td>,
        ];
    },
    renderContentRelatables: (component: EntityRelationsEditor) => {
        return (
            <div
                style={{
                    width: '450px',
                    height: '350px',
                    marginTop: '10x',
                    overflowY: 'scroll',
                    overflowX: 'scroll',
                }}
            >
                <table className="table">
                    <tbody>
                        {component.state.relatables.map((entity) => (
                            <tr key={'candidate-' + entity['@eid']}>
                                {component.props.renderRelatable(
                                    component,
                                    entity,
                                    getEntityRelationStatus(
                                        component.props.specification,
                                        component.props.baseChanges,
                                        component.state.changes,
                                        entity
                                    )
                                )}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    },
    renderContentLoading: (component: EntityRelationsEditor) => {
        return (
            <div
                style={{
                    width: '450px',
                    height: '350px',
                    marginTop: '10x',
                }}
            >
                {component.context.translate('loading')}
            </div>
        );
    },
    renderButtonValidate: (component: EntityRelationsEditor) => {
        let total =
            component.state.baseCount +
            component.state.changes.added.length -
            component.state.changes.removed.length;
        return (
            <button
                className="btn btn-primary"
                onClick={(e) =>
                    component.props.validate(component.state.changes)
                }
            >
                <span>
                    {component.context.translate('Validate') +
                        ` ${total} ${component.context.translate(
                            total >= 2 ? 'entities' : 'entity'
                        )}`}
                </span>
                {component.state.changes.added.length > 0 ||
                component.state.changes.removed.length > 0 ? (
                    <span>
                        {' ('}
                        {component.state.changes.added.length > 0 ? (
                            <span style={{color: 'green'}}>{`${
                                component.state.changes.added.length
                            } ${component.context.translate('added')}`}</span>
                        ) : null}
                        {component.state.changes.removed.length > 0 &&
                        component.state.changes.added.length > 0 ? (
                            <span>{', '}</span>
                        ) : null}
                        {component.state.changes.removed.length > 0 ? (
                            <span style={{color: 'red'}}>{`${
                                component.state.changes.removed.length
                            } ${component.context.translate('removed')}`}</span>
                        ) : null}
                        {')'}
                    </span>
                ) : null}
            </button>
        );
    },
    renderButtonCancel: (component: EntityRelationsEditor) => {
        return (
            <button
                className="btn btn-primary"
                onClick={(e) => component.props.cancel()}
            >
                <span>{component.context.translate('Cancel')}</span>
            </button>
        );
    },
    render: (component: EntityRelationsEditor) => {
        return (
            <div className={component.props.className}>
                <div>{component.props.renderLookup(component)}</div>
                <div>
                    {component.state.isLoadingRelatables
                        ? component.props.renderContentLoading(component)
                        : component.props.renderContentRelatables(component)}
                </div>
                <div>
                    {component.props.renderButtonCancel(component)}
                    {component.props.renderButtonValidate(component)}
                </div>
            </div>
        );
    },
};

/**
 * The default editors of relations for an entity
 */
export class DefaultEntityRelationsEditor
    extends React.Component<
        DefaultEntityRelationsEditorProps,
        EntityRelationsEditorState
    >
    implements
        EntityRelationsEditor<
            DefaultEntityRelationsEditorProps,
            EntityRelationsEditorState
        >
{
    public static contextType = TranslationContext;
    public context!: React.ContextType<typeof TranslationContext>;
    public static defaultProps = ENTITY_RELATIONS_EDITOR_DEFAULT_PROPS;
    state: EntityRelationsEditorState;
    _isMounted: boolean;

    constructor(props: Readonly<DefaultEntityRelationsEditorProps>) {
        super(props);
        this._isMounted = false;
        this.state = {
            baseCount: 0,
            changes: {added: [], removed: []},
            isLoadingRelatables: false,
            relatables: [],
        };
        this.onLookupLoading = this.onLookupLoading.bind(this);
        this.onLookupResult = this.onLookupResult.bind(this);
        this.onToggle = this.onToggle.bind(this);
    }

    componentDidMount() {
        this._isMounted = true;
        let self = this;
        getRelatedCount(this.props.specification)
            .then((count) => {
                let finalCount =
                    count +
                    self.props.baseChanges.added.length -
                    self.props.baseChanges.removed.length;
                if (self._isMounted) {
                    self.setState({baseCount: finalCount});
                }
            })
            .catch((reason) => {
                console.log(reason);
            });
        lookupEntitiesRelated(
            this.props.specification,
            this.props.lookupLimitRelated
        )
            .then((entities) => {
                if (self._isMounted) {
                    self.setState({relatables: entities});
                }
            })
            .catch((reason) => {
                console.log(reason);
            });
    }

    componentWillUnmount() {
        this._isMounted = false;
    }

    onLookupLoading() {
        this.setState({isLoadingRelatables: true});
    }

    onLookupResult(result: providers.Entity[]) {
        if (this._isMounted) {
            this.setState({isLoadingRelatables: false, relatables: result});
        }
    }

    onToggle(entity: providers.Entity, isRelated: boolean) {
        if (isRelated) {
            // should be added
            let index = this.state.changes.removed.findIndex(
                (e) => e['@eid'] === entity['@eid']
            );
            if (index >= 0) {
                // was previously removed
                this.setState({
                    changes: {
                        added: this.state.changes.added,
                        removed: this.state.changes.removed.filter(
                            (e, i) => i !== index
                        ),
                    },
                });
            } else {
                // add
                this.setState({
                    changes: {
                        added: this.state.changes.added.concat(entity),
                        removed: this.state.changes.removed,
                    },
                });
            }
        } else {
            // should be removed
            let index = this.state.changes.added.findIndex(
                (e) => e['@eid'] === entity['@eid']
            );
            if (index >= 0) {
                // was previously added
                this.setState({
                    changes: {
                        added: this.state.changes.added.filter(
                            (e, i) => i !== index
                        ),
                        removed: this.state.changes.removed,
                    },
                });
            } else {
                // remove
                this.setState({
                    changes: {
                        added: this.state.changes.added,
                        removed: this.state.changes.removed.concat(entity),
                    },
                });
            }
        }
    }

    render(): JSX.Element {
        return this.props.render(this);
    }
}
